import React, { useState, useRef } from "react";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

function ReservFormUpload() {
  const fileInputRef = useRef();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("idle"); // idle, uploaded, processing
  const [error, setError] = useState(""); // 錯誤訊息
  const [openDialog, setOpenDialog] = useState(false); // 控制 Dialog
  const [library, setLibrary] = useState("鹽水區圖書館"); // 新增館別 state

  const handleBoxClick = () => {
    fileInputRef.current.click();
  };

  const handleDownloadBtnClick = async () => {
    if (!selectedFile || !library) return;
    setUploadStatus("processing");
    setError("");
    try {
      const formData = new FormData();
      formData.append("uploadfile", selectedFile);
      formData.append("keepSite", library);

      const response = await fetch("https://v2.api.tnml-tools.lch.tw/circulation/interlibrary/reservationReport", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {

        throw new Error("伺服器處理失敗，請稍後再試");
      }

      // 取得檔案名稱
      let filename = "找書單.pdf";

      const disposition = response.headers.get("Content-Disposition");
      if (disposition) {
        const match = disposition.match(/filename\s*=\s*"?([^"]+)"?/i);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1].trim());
        }
      }

      // 下載 PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setUploadStatus("idle");
      setSelectedFile(null);
    } catch (err) {
      setUploadStatus("idle");
      setSelectedFile(null);
      setError(err.message || "下載失敗，請稍後再試");
      setOpenDialog(true);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isCSV = file.name.toLowerCase().endsWith(".csv");
      if (isCSV) {
        setSelectedFile(file);
        setUploadStatus("uploaded")
      } else {
        setError("檔案格式有誤: 請上傳 CSV 格式的檔案");
        setOpenDialog(true);
        setSelectedFile(null);
        setUploadStatus("idle");
      }
    }
  };

  // 狀態對應顏色
  let borderColor = "#90caf9";
  let iconColor = "#1976d2";
  let textColor = "#1976d2";
  if (uploadStatus === "uploaded") {
    borderColor = "#43a047";
    iconColor = "#43a047";
    textColor = "#388e3c";
  } else if (uploadStatus === "processing") {
    borderColor = "#ffa726";
    iconColor = "#ffa726";
    textColor = "#f57c00";
  }

  return (
    <div style={{ height: "100%", position: "relative" }}>
      {/* 館別下拉選單 */}
      <div style={{ marginBottom: 24, textAlign: "left" }}>
        <label htmlFor="library-select" style={{ fontWeight: "bold", marginRight: 12 }}>
          選擇館別：
        </label>
        <select
          id="library-select"
          value={library}
          onChange={e => setLibrary(e.target.value)}
          style={{
            padding: "8px 16px",
            borderRadius: 6,
            border: "1px solid #90caf9",
            fontSize: 16,
            minWidth: 160,
          }}
        >
          <option value="鹽水區圖書館">鹽水區圖書館</option>
          {/* 你可以依需求增加更多館別 */}
        </select>
      </div>
      {/* 彈跳對話框 */}
      <Dialog open={openDialog} onClose={() => { }} disableEscapeKeyDown>
        <DialogTitle>發生錯誤</DialogTitle>
        <DialogContent>
          {error}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} autoFocus>
            確定
          </Button>
        </DialogActions>
      </Dialog>
      <div
        onClick={handleBoxClick}
        style={{
          width: "100%",
          flex: 1,
          border: `2px ${uploadStatus === "uploaded" ? "solid" : "dashed"} ${borderColor}`,
          borderRadius: 12,
          padding: "40px 0 80px 0", // 下方多預留空間給按鈕
          textAlign: "center",
          color: textColor,
          background: "#f4f8ff",
          cursor: uploadStatus === "processing" ? "not-allowed" : "pointer",
          transition: "border-color 0.2s, color 0.2s",
          opacity: uploadStatus === "processing" ? 0.7 : 1,
          boxSizing: "border-box",
          minHeight: 0,
        }}
      >
        <CloudUploadIcon
          style={{
            fontSize: 48,
            marginBottom: 8,
            color: iconColor,
          }}
        />
        <div style={{ fontSize: 18, fontWeight: "bold" }}>
          {uploadStatus === "idle" && !selectedFile && (
            <>
              請上傳流通系統輸出的預約報表
            </>
          )}
          {uploadStatus === "processing" && (
            <span style={{ color: "#f57c00" }}>伺服器處理中，請稍候...</span>
          )}
          {uploadStatus === "uploaded" && selectedFile && (
            <span>
              選擇了檔案:"<span style={{ color: "#388e3c" }}>{selectedFile.name}</span>"
            </span>
          )}
        </div>
        <input
          type="file"
          accept=".csv"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
          disabled={uploadStatus === "uploading"}
        />
      </div>
      {/* 下一步按鈕：一直顯示，僅上傳成功時可點選 */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleDownloadBtnClick}
        disabled={uploadStatus !== "uploaded"}
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          borderRadius: "24px",
          fontSize: 18,
          fontWeight: "bold",
          px: 4,
          py: 1.5,
          boxShadow: 2,
        }}
      >
        產生並下載找書單
      </Button>
    </div>
  );
}

function ReservForm() {

  return (
    <div
      style={{
        maxWidth: 1080,
        width: "100%",
        minHeight: 420, // 固定最小高度
        height: 420, // 固定高度（可依需求調整）
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        padding: 24,
        margin: "0 auto",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ReservFormUpload />
    </div>
  );
}

export default ReservForm;